import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { AppName, SystemEnvironmentKind, TestSystemFavicon } from '../constants';
import {
  systemEnvironmentKindForHost,
  systemEnvironmentLabel,
  systemEnvironmentTitlePrefix,
} from '../helpers/system-environment';

/**
 * The ONLY place that knows whether this page is a TEST, DEV or Live system. Decided once, from
 * the page's host name - never from a setting - so a Live system can never be marked by mistake
 * and a test system can never lose its marking through configuration.
 */
@Injectable({ providedIn: 'root' })
export class SystemEnvironmentService {
  private readonly document = inject(DOCUMENT);

  public readonly kind: SystemEnvironmentKind = systemEnvironmentKindForHost(this.document.location?.hostname);
  public readonly isMarked = this.kind !== SystemEnvironmentKind.Live;
  public readonly label = systemEnvironmentLabel(this.kind);
  public readonly titlePrefix = systemEnvironmentTitlePrefix(this.kind);

  /** Every browser title goes through here, so a TEST / DEV tab is recognisable on its own. */
  pageTitle(header: string): string {
    return `${this.titlePrefix}${AppName.tmsWeb} - ${header}`;
  }

  applyToDocument(): void {
    // Live renders exactly as it always has - nothing in the page is touched.
    if (!this.isMarked) {
      return;
    }

    // Called at start-up: index.html's static title shows until the first screen sets its own,
    // and the tab icon is all a user sees of a tab squeezed among many, so both carry the marking
    // from the very first paint. The prefix check keeps a repeated call from doubling it.
    if (!this.document.title.startsWith(this.titlePrefix)) {
      this.document.title = `${this.titlePrefix}${this.document.title}`;
    }

    let icon = this.document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) {
      icon = this.document.createElement('link');
      icon.setAttribute('rel', 'icon');
      this.document.head.appendChild(icon);
    }

    icon.setAttribute('type', 'image/svg+xml');
    icon.setAttribute('href', TestSystemFavicon);
  }
}
